import { useEffect } from 'react';
import { Link, useRouteError } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { RefreshCw, Home } from 'lucide-react';
import { Button, Container } from '@easymoving/ui';

/**
 * Route-level error screen (React Router errorElement). Two jobs:
 * 1. A lazy-chunk fetch failure (dev server restarted under an open tab, or a
 *    fresh prod deploy invalidating an old tab's hashed chunk URLs) is fixed
 *    by reloading — do that automatically, once per 10s, before showing UI.
 * 2. Anything else gets a friendly localized screen instead of React Router's
 *    raw "Unexpected Application Error!" — with reload + home actions.
 */
const RELOAD_KEY = 'easymoving.chunkReload';

function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : '';
  return /dynamically imported module|Importing a module script failed|Failed to fetch/i.test(
    message,
  );
}

export function RouteErrorBoundary() {
  const { t } = useTranslation();
  const error = useRouteError();

  const chunkError = isChunkLoadError(error);
  useEffect(() => {
    if (!chunkError) return;
    let last = 0;
    try {
      last = Number(sessionStorage.getItem(RELOAD_KEY) || 0);
    } catch {
      /* ignore */
    }
    if (Date.now() - last > 10_000) {
      try {
        sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
      } catch {
        /* ignore */
      }
      window.location.reload();
    }
  }, [chunkError]);

  return (
    <Container size="narrow" className="py-16 text-center" data-testid="route-error">
      <div className="mx-auto max-w-sm flex flex-col items-center gap-4">
        <span className="size-12 rounded-full bg-amber-tint text-amber flex items-center justify-center">
          <RefreshCw className="size-6" />
        </span>
        <div>
          <h1 className="font-display text-xl tracking-snug">{t('errors.title')}</h1>
          <p className="text-graphite text-[14px] mt-1">{t('errors.body')}</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => window.location.reload()}>
            <RefreshCw className="size-4" />
            {t('errors.reload')}
          </Button>
          <Button asChild variant="secondary">
            <Link to="/app" reloadDocument>
              <Home className="size-4" />
              {t('errors.home')}
            </Link>
          </Button>
        </div>
      </div>
    </Container>
  );
}
